import type { EntityOfType } from '@cognitoforms/model.js';
import type { EntryView } from '@cognitoforms/types/server-types/forms/model/entry-view';
import type { EntryViewFilter } from '@cognitoforms/types/server-types/forms/model/entry-view-filter';
import type { FormEntryIndex } from '@cognitoforms/types/server-types/forms/model/form-entry-index';
import type { EntryKey } from './entry-view-types';

export function cloneEntryView(view: EntryView): EntryView {
	return JSON.parse(JSON.stringify(view));
}

export function normalizeArray<T>(values: T[] = []): T[] {
	return values.slice().sort();
}

export function getKeyword(view: EntryView): string {
	return view?.Filter?.Keyword || '';
}

export function getServerCriteriaSignature(view: EntryView): string {
	if (!view)
		return '';

	const filter = view.Filter || {} as EntryViewFilter;
	return JSON.stringify({
		RoleId: view.RoleId,
		SharedWithMe: !!view.SharedWithMe,
		IsUserSpecific: !!view.IsUserSpecific,
		Type: view.Type,
		SortBy: (view.SortBy || []).map(sort => ({ FieldId: sort.FieldId, Ascending: !!sort.Ascending })),
		Filter: {
			ScopeId: filter.ScopeId || '',
			Expression: filter.Expression || '',
			Invalid: !!filter.Invalid,
			IsDynamic: !!filter.IsDynamic,
			EntryStatus: normalizeArray(filter.EntryStatus || []),
			PaymentStatus: normalizeArray(filter.PaymentStatus || [])
		},
		IsTaskView: !!view.IsTaskView,
		TaskSummaryField: view.TaskSummaryField || '',
		TaskDescriptionField: view.TaskDescriptionField || '',
		TaskDueDateField: view.TaskDueDateField || '',
		StaticTaskDueDateTime: view.StaticTaskDueDateTime || null,
		StaticTaskDueDateString: view.StaticTaskDueDateString || '',
		TaskPriority: view.TaskPriority || 0,
		SendTaskReminders: !!view.SendTaskReminders,
		TaskReminders: view.TaskReminders || []
	});
}

export function hasKeywordOnlyChange(baseView: EntryView, view: EntryView): boolean {
	return getServerCriteriaSignature(baseView) === getServerCriteriaSignature(view)
		&& getKeyword(baseView) !== getKeyword(view);
}

export function hasSavedKeywordChange(baseView: EntryView, view: EntryView): boolean {
	return !!getKeyword(baseView)
		&& getKeyword(baseView) !== getKeyword(view);
}

export function hasServerCriteriaChange(baseView: EntryView, view: EntryView): boolean {
	return getServerCriteriaSignature(baseView) !== getServerCriteriaSignature(view);
}

export function hasSortChange(baseView: EntryView, view: EntryView): boolean {
	return JSON.stringify((baseView?.SortBy || []).map(sort => ({ FieldId: sort.FieldId, Ascending: !!sort.Ascending })))
		!== JSON.stringify((view?.SortBy || []).map(sort => ({ FieldId: sort.FieldId, Ascending: !!sort.Ascending })));
}

export function needsTemporaryEntryView(baseView: EntryView, view: EntryView): boolean {
	return hasServerCriteriaChange(baseView, view) || hasSavedKeywordChange(baseView, view);
}

export function getFilterCacheKey(formId: string, filter: EntryViewFilter): string {
	return JSON.stringify({
		formId,
		ScopeId: filter?.ScopeId || '',
		Keyword: filter?.Keyword || '',
		Expression: filter?.Expression || '',
		ExpressionSummary: filter?.ExpressionSummary || '',
		Invalid: !!filter?.Invalid,
		IsDynamic: !!filter?.IsDynamic,
		EntryStatus: normalizeArray(filter?.EntryStatus || []),
		PaymentStatus: normalizeArray(filter?.PaymentStatus || [])
	});
}

export function getViewId(index: EntityOfType<FormEntryIndex> | FormEntryIndex) {
	return index.Id.split('|')[0];
}

export function getTimestamp(index: EntityOfType<FormEntryIndex> | FormEntryIndex) {
	return index.Id.split('|')[1];
}

export function getEntryId(index: EntityOfType<FormEntryIndex> | FormEntryIndex) {
	return index.Id.split('|')[2];
}

export function keyFromIndex({ Id, EntryETag, OrderETag, OrderId }: EntityOfType<FormEntryIndex> | FormEntryIndex): EntryKey {
	let EntryId = Id.split('|')[2];
	let Scope = '';
	const dot = EntryId.indexOf('.');
	if (dot > 0) {
		Scope = EntryId.substr(dot + 1);
		EntryId = EntryId.substr(0, dot);
	}
	return {
		EntryId,
		Scope,
		EntryETag,
		OrderETag,
		OrderId
	};
}

export function createBlankViewFilter(): EntryViewFilter {
	return {
		ScopeId: '',
		Keyword: '',
		Expression: '',
		ExpressionSummary: '',
		Invalid: false,
		IsDynamic: false,
		EntryStatus: [],
		PaymentStatus: []
	} as EntryViewFilter;
}